/**
 * SegmentedVideo — renders a live camera preview onto a canvas with
 * MediaPipe selfie-segmentation, replacing the background with either:
 *  - a CSS gradient (gradient string)
 *  - a static image URL (imageUrl)
 *  - a soft blur of the camera (mode="blur")
 *
 * If MediaPipe fails to load (offline / older browser), or if no bg is
 * provided, it gracefully falls back to a plain mirrored video draw.
 *
 * Props:
 *  stream     — MediaStream from getUserMedia
 *  mirror     — true for selfie/front camera (default true)
 *  filterCss  — CSS filter applied to the foreground (e.g. "saturate(1.6)")
 *  gradient   — CSS linear-gradient(...) string (extracts hex stops)
 *  imageUrl   — bg image URL
 *  blur       — boolean — strong blur the camera as the bg
 *  className  — extra classes for the canvas
 *  onCanvas   — callback(canvasEl) for thumbnail capture
 */
import React, { useEffect, useRef } from "react";

const SEG_MODEL = "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite";
const WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

let segPromise = null;
function loadSeg() {
  if (segPromise) return segPromise;
  segPromise = (async () => {
    try {
      const mp = await import("@mediapipe/tasks-vision");
      const fileset = await mp.FilesetResolver.forVisionTasks(WASM_BASE);
      return await mp.ImageSegmenter.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: SEG_MODEL, delegate: "GPU" },
        runningMode: "VIDEO",
        outputCategoryMask: true,
      });
    } catch (e) {
      console.warn("[SegmentedVideo] segmenter load failed", e);
      return null;
    }
  })();
  return segPromise;
}

export default function SegmentedVideo({
  stream, mirror = true, filterCss = "none",
  gradient = null, imageUrl = null, blur = false,
  className = "", onCanvas,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const segRef = useRef(null);
  const rafRef = useRef(0);
  const bgImgRef = useRef(null);

  const hasBg = !!(gradient || imageUrl || blur);

  // Lazy-load segmenter only when bg replacement is needed
  useEffect(() => {
    if (!hasBg) return;
    let alive = true;
    loadSeg().then(s => { if (alive) segRef.current = s; });
    return () => { alive = false; };
  }, [hasBg]);

  // Wire stream to video element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (stream && v.srcObject !== stream) {
      v.srcObject = stream;
      v.muted = true;
      v.play().catch(() => {});
    }
    if (!stream) v.srcObject = null;
  }, [stream]);

  // Preload bg image
  useEffect(() => {
    if (!imageUrl) { bgImgRef.current = null; return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { bgImgRef.current = img; };
    img.onerror = () => { bgImgRef.current = null; };
    img.src = imageUrl;
  }, [imageUrl]);

  // Render loop
  useEffect(() => {
    if (!stream) {
      // Clear canvas when no stream
      const cv = canvasRef.current;
      if (cv) cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
      return;
    }
    const v = videoRef.current;
    const cv = canvasRef.current;
    if (!v || !cv) return;
    cv.width = 720;
    cv.height = 1280;
    const ctx = cv.getContext("2d");

    if (onCanvas) onCanvas(cv);

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      if (!v.videoWidth) return;

      const vw = v.videoWidth, vh = v.videoHeight;
      const targetAR = cv.width / cv.height;
      const srcAR = vw / vh;
      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (srcAR > targetAR) { sw = vh * targetAR; sx = (vw - sw) / 2; }
      else                  { sh = vw / targetAR; sy = (vh - sh) / 2; }

      ctx.clearRect(0, 0, cv.width, cv.height);

      // 1) Draw the foreground video (mirrored if selfie)
      ctx.save();
      if (mirror) { ctx.translate(cv.width, 0); ctx.scale(-1, 1); }
      ctx.filter = filterCss;
      ctx.drawImage(v, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
      ctx.restore();

      // 2) Background replacement (if requested and segmenter ready)
      if (hasBg && segRef.current) {
        try {
          const ts = performance.now();
          const result = segRef.current.segmentForVideo(v, ts);
          const mask = result.categoryMask;
          if (mask) {
            const md = mask.getAsUint8Array();
            const mw = mask.width, mh = mask.height;
            const tmp = document.createElement("canvas");
            tmp.width = mw; tmp.height = mh;
            const tctx = tmp.getContext("2d");
            const id = tctx.createImageData(mw, mh);
            for (let i = 0; i < md.length; i++) {
              const a = md[i] === 0 ? 255 : 0; // 0 = person
              id.data[i*4]     = 255;
              id.data[i*4 + 1] = 255;
              id.data[i*4 + 2] = 255;
              id.data[i*4 + 3] = a;
            }
            tctx.putImageData(id, 0, 0);

            const mxR = mw / vw, myR = mh / vh;

            // Build the new background canvas (cv-sized)
            const bgC = document.createElement("canvas");
            bgC.width = cv.width; bgC.height = cv.height;
            const bgCtx = bgC.getContext("2d");
            if (blur) {
              bgCtx.save();
              if (mirror) { bgCtx.translate(cv.width, 0); bgCtx.scale(-1, 1); }
              bgCtx.filter = "blur(22px) brightness(0.85)";
              bgCtx.drawImage(v, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
              bgCtx.restore();
            } else if (bgImgRef.current && bgImgRef.current.naturalWidth) {
              const img = bgImgRef.current;
              const ar = img.naturalWidth / img.naturalHeight;
              let dW = cv.width, dH = cv.width / ar;
              if (dH < cv.height) { dH = cv.height; dW = cv.height * ar; }
              bgCtx.drawImage(img, (cv.width - dW)/2, (cv.height - dH)/2, dW, dH);
            } else if (gradient) {
              const grd = bgCtx.createLinearGradient(0, 0, cv.width, cv.height);
              const colors = (gradient.match(/#[0-9A-Fa-f]{6}/g)) || ["#000000", "#222222"];
              colors.forEach((c, i) => grd.addColorStop(i / Math.max(1, colors.length - 1), c));
              bgCtx.fillStyle = grd;
              bgCtx.fillRect(0, 0, cv.width, cv.height);
            }

            // Erase non-person pixels then paint bg behind
            ctx.save();
            ctx.globalCompositeOperation = "destination-in";
            if (mirror) { ctx.translate(cv.width, 0); ctx.scale(-1, 1); }
            ctx.drawImage(tmp, sx * mxR, sy * myR, sw * mxR, sh * myR, 0, 0, cv.width, cv.height);
            ctx.restore();
            ctx.save();
            ctx.globalCompositeOperation = "destination-over";
            ctx.drawImage(bgC, 0, 0);
            ctx.restore();
          }
        } catch { /* swallow per-frame errors */ }
      }
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [stream, mirror, filterCss, gradient, imageUrl, blur, hasBg, onCanvas]);

  return (
    <>
      <video ref={videoRef} playsInline autoPlay muted className="hidden"/>
      <canvas ref={canvasRef} className={className} data-testid="segmented-video-canvas"/>
    </>
  );
}
